import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSWRConfig } from 'swr';
import { toast } from '@/components';
import {
    CACHE_KEYS,
    getSourceCacheKey,
    getSourceEventsCacheKey,
    isSourcesCacheKey,
} from '@/constants/cache-keys';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { EngineService } from '@/services/engine';
import { cleanupSourceAnalysisData } from '@/stores';
import { useSourcesStore } from '@/stores/sources.store';
import { FullSource, SourceStatus } from '@/types/source';
import { isConflictError } from '@/utils/http';
import {
    revalidateByCacheKeyPrefix,
    revalidateIdentityViews,
} from '@/utils/swr';
import { revalidateSourceList } from '@/hooks/useSourceListInfinite';

export function useSourceReanalysis() {
    const { t } = useTranslation(I18nNamespace.SOURCES);
    const { mutate } = useSWRConfig();
    const updateSource = useSourcesStore((state) => state.updateSource);
    const [isStartingAnalysis, setIsStartingAnalysis] = useState(false);

    const reanalyzeSource = async (source: FullSource) => {
        if (isStartingAnalysis) return;

        setIsStartingAnalysis(true);
        try {
            await EngineService.startAnalysis(source.id);
            await mutate<FullSource>(
                getSourceCacheKey(source.id),
                (current) => ({
                    ...(current ?? source),
                    status: SourceStatus.PENDING,
                    progress: 0,
                }),
                { revalidate: false },
            );
            updateSource(source.id, {
                status: SourceStatus.PENDING,
                progress: 0,
            });
            cleanupSourceAnalysisData(source.id);
            await Promise.allSettled([
                mutate(getSourceCacheKey(source.id)),
                revalidateSourceList(),
                mutate(
                    (key) =>
                        Array.isArray(key) &&
                        key[0] === CACHE_KEYS.SOURCE_FOLDER_CHILDREN,
                ),
                mutate(isSourcesCacheKey),
                revalidateByCacheKeyPrefix(getSourceEventsCacheKey(source.id)),
                revalidateByCacheKeyPrefix(CACHE_KEYS.REPORT_EVENTS_PREFIX),
                revalidateByCacheKeyPrefix(CACHE_KEYS.REPORT_EVENT_PREFIX),
                revalidateByCacheKeyPrefix(CACHE_KEYS.REPORT_PIPELINE_PREFIX),
                revalidateByCacheKeyPrefix(CACHE_KEYS.REPORTS_LIST),
                revalidateIdentityViews(),
            ]);
            toast.success(t('toast.reanalyzeSuccess'));
        } catch (error) {
            toast.error(
                isConflictError(error)
                    ? t('toast.reanalyzeInProgress')
                    : t('toast.reanalyzeError'),
            );
        } finally {
            setIsStartingAnalysis(false);
        }
    };

    return { reanalyzeSource, isStartingAnalysis };
}
